import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldHalf, Users, Globe2, Banknote, ClipboardCheck, Ban, ScrollText, Loader2, WifiOff, Wifi, Radio, CloudSun, Clock, Megaphone, Gavel, UserX, UserCheck, Snowflake, HeartPulse, ArrowDownToDot, ArrowUpFromDot, AlertTriangle, Flag, Copy, X, Fingerprint, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, DEMO } from "@/components/cef/cefShared";

/* In-game ADMIN SUITE (CEF) — /cef/admin/:serverId?token=<admin-role session>.
   Transparent side-dock layout: a slim vertical dock floats over the game world and
   panels are frosted glass, so staff keep eyes on the scene while moderating.
   The bridge mints admin sessions for staff playercodes. Moderation/world commands queue
   to the hub; nc_bridge.py polls every 5s and executes them on the live server.
   Player cards expose Steam ID / IP and one-tap filing of Specpixel-network watch reports
   (studio staff approve them on the web admin → flag appears on EVERY server's console).
   No token (browser preview) → rich demo console so owners can see exactly what staff get. */

const ACCENT = "#f43f5e";
const WEATHERS = ["clear", "rain", "storm", "fog", "overcast", "snow"];
const ACTIONS = [
  { id: "warn", label: "Warn", Icon: AlertTriangle, color: "#fbbf24", needsReason: true },
  { id: "kick", label: "Kick", Icon: UserX, color: "#fb923c", needsReason: true },
  { id: "ban", label: "Ban", Icon: Gavel, color: "#ef4444", needsReason: true },
  { id: "bring", label: "Bring", Icon: ArrowDownToDot, color: "#22d3ee", needsReason: false },
  { id: "goto", label: "Goto", Icon: ArrowUpFromDot, color: "#22d3ee", needsReason: false },
  { id: "freeze", label: "Freeze", Icon: Snowflake, color: "#93c5fd", needsReason: false },
  { id: "heal", label: "Heal", Icon: HeartPulse, color: "#34d399", needsReason: false },
];
const JOB_CLS = { police: "text-nc-blue border-nc-blue/40", ems: "text-nc-green border-nc-green/40", fire: "text-nc-orange border-nc-orange/40", civ: "text-zinc-400 border-white/15" };
const CMD_STATUS = { pending: "text-amber-400 border-amber-500/40", done: "text-emerald-400 border-emerald-500/40", failed: "text-red-400 border-red-500/40" };
const REPORT_CATEGORIES = ["hacking", "cheating/exploits", "griefing", "toxicity", "harassment", "ban evasion", "other"];
const REP_STATUS = { pending: "text-amber-400 border-amber-500/40", approved: "text-emerald-400 border-emerald-500/40", rejected: "text-red-400 border-red-500/40" };

const copyText = (v, what) => {
  try { navigator.clipboard.writeText(String(v)); toast.success(`${what} copied`); }
  catch { toast.error("Clipboard blocked"); }
};

export default function AdminCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [ov, setOv] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("live");
  const [commands, setCommands] = useState([]);
  const [bans, setBans] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [reports, setReports] = useState([]);
  const [action, setAction] = useState(null); // {actionId, target}
  const [reason, setReason] = useState("");
  const [announce, setAnnounce] = useState("");
  const [weather, setWeather] = useState("rain");
  const [hour, setHour] = useState(23);
  const [eco, setEco] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState("");
  const [selPlayer, setSelPlayer] = useState(null);
  const [netrep, setNetrep] = useState({ target: null, category: "griefing", reason: "", evidence_url: "", evidence_text: "" });

  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toLocaleTimeString([], { hour12: false })), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(() => {
    if (demo) { setOv(DEMO.admin.overview); setCommands(DEMO.admin.commands); setErr(""); return; }
    api.get(`/nc/servers/${serverId}/admin/overview`, { params: { token } })
      .then((r) => { setOv(r.data); setErr(""); if (!eco) setEco(r.data.economy || {}); })
      .catch((e) => setErr(e.response?.data?.detail || "Admin console offline"));
  }, [demo, serverId, token, eco]);

  const loadLog = useCallback(() => {
    if (demo) { setCommands(DEMO.admin.commands); return; }
    api.get(`/nc/servers/${serverId}/admin/commands`, { params: { token } }).then((r) => setCommands(r.data.commands || [])).catch(() => {});
  }, [demo, serverId, token]);

  const loadBans = useCallback(() => {
    if (demo) { setBans(DEMO.admin.bans); return; }
    api.get(`/nc/servers/${serverId}/admin/bans`, { params: { token } }).then((r) => setBans(r.data.bans || [])).catch(() => {});
  }, [demo, serverId, token]);

  const loadWhitelist = useCallback(() => {
    if (demo) { setWhitelist(DEMO.admin.whitelist); return; }
    api.get(`/nc/servers/${serverId}/admin/whitelist`, { params: { token } }).then((r) => setWhitelist(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [demo, serverId, token]);

  const loadReports = useCallback(() => {
    if (demo) { setReports(DEMO.admin.network_reports); return; }
    api.get(`/nc/servers/${serverId}/admin/network-reports`, { params: { token } }).then((r) => setReports(r.data.reports || [])).catch(() => {});
  }, [demo, serverId, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (tab === "log") loadLog();
    if (tab === "bans") loadBans();
    if (tab === "whitelist") loadWhitelist();
    if (tab === "network") loadReports();
    if (tab === "live") loadLog();
  }, [tab, loadLog, loadBans, loadWhitelist, loadReports]);

  const sendCommand = async (type, target = {}, params = {}, reasonText = "") => {
    const payload = { type, target_playercode: target.playercode || 0, target_name: target.name || "", reason: reasonText, params };
    if (demo) {
      toast.success(`Preview: "${type}" would hit the server in under 5s via the bridge`);
      setCommands((c) => [{ id: `demo-${Date.now()}`, ...payload, by: "pc:37049 (you)", status: "pending", created_at: new Date().toISOString() }, ...c]);
      return true;
    }
    try {
      await api.post(`/nc/servers/${serverId}/admin/commands`, payload, { params: { token } });
      toast.success(`${type} queued — bridge executes within seconds`);
      loadLog();
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };

  const confirmAction = async () => {
    if (!action) return;
    setBusy(true);
    const ok = await sendCommand(action.actionId, action.target, {}, reason);
    setBusy(false);
    if (ok) { setAction(null); setReason(""); }
  };

  const decide = async (appId, status) => {
    if (demo) { toast.success(`Preview: application would be ${status}`); setWhitelist((w) => w.filter((a) => a.id !== appId)); return; }
    try {
      await api.post(`/nc/servers/${serverId}/admin/whitelist/${appId}/decision`, { status }, { params: { token } });
      toast.success(`Application ${status}`);
      loadWhitelist();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveEconomy = async () => {
    if (demo) { toast.success("Preview: economy would update and the bridge would apply it on next config pull"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/nc/servers/${serverId}/admin/economy`, eco, { params: { token } });
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const openReportFor = (p) => {
    setNetrep({ target: p, category: "griefing", reason: "", evidence_url: "", evidence_text: "" });
    setSelPlayer(null);
    setTab("network");
  };

  const fileReport = async () => {
    const t = netrep.target;
    if (!t) { toast.error("Pick a player from the live roster first"); return; }
    if (netrep.reason.trim().length < 10) { toast.error("Reason needs at least 10 characters"); return; }
    if (!netrep.evidence_url.trim() && netrep.evidence_text.trim().length < 20) { toast.error("Attach evidence — a clip link or a 20+ char statement"); return; }
    const payload = { target_playercode: t.playercode, target_name: t.name || "", target_steamid: t.steamid || "", category: netrep.category, reason: netrep.reason.trim(), evidence_url: netrep.evidence_url.trim(), evidence_text: netrep.evidence_text.trim() };
    if (demo) {
      toast.success(`Preview: network report on ${t.name} would go to Specpixel staff for review`);
      setReports((r) => [{ id: `demo-${Date.now()}`, server_name: ov?.server?.name || "this server", ...payload, status: "pending", created_at: new Date().toISOString(), reviewed_by: "", review_note: "" }, ...r]);
      setNetrep({ target: null, category: "griefing", reason: "", evidence_url: "", evidence_text: "" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/admin/network-report`, payload, { params: { token } });
      toast.success(r.data.message);
      setNetrep({ target: null, category: "griefing", reason: "", evidence_url: "", evidence_text: "" });
      loadReports();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const world = ov?.world || {};
  const TABS = [
    ["live", Users, "Live Ops"], ["world", Globe2, "World"], ["economy", Banknote, "Economy"],
    ["whitelist", ClipboardCheck, `Whitelist${ov?.counts?.pending_whitelist ? ` (${ov.counts.pending_whitelist})` : ""}`],
    ["bans", Ban, `Bans${ov?.counts?.active_bans ? ` (${ov.counts.active_bans})` : ""}`],
    ["network", Flag, "Network"],
    ["log", ScrollText, "Command Log"],
  ];

  return (
    <div data-testid="cef-admin" className="fixed inset-0 text-zinc-100 pointer-events-none" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="admin console" />}

      {/* transparent side dock — floats over the game world */}
      <aside data-testid="admin-dock" className="pointer-events-auto absolute left-3 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-1.5 py-3 px-2 rounded-2xl border border-white/15 bg-black/55 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]" style={{ marginTop: demo ? 14 : 0 }}>
        <div className="relative mb-1.5">
          <ShieldHalf className="w-5 h-5" style={{ color: ACCENT }} />
          <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${ov?.live ? "bg-emerald-400" : "bg-zinc-600"}`} />
        </div>
        {TABS.map(([id, Icon, label]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} title={label}
            className={`relative group w-11 h-11 rounded-xl border flex items-center justify-center transition-all ${tab === id ? "text-black scale-105" : "text-zinc-400 border-white/10 hover:border-white/35 hover:text-zinc-100"}`}
            style={tab === id ? { backgroundColor: ACCENT, borderColor: ACCENT, boxShadow: `0 0 18px ${ACCENT}66` } : {}}>
            <Icon className="w-4 h-4" />
            <span className="pointer-events-none absolute left-[52px] top-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.2em] bg-black/85 border border-white/15 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-40">{label}</span>
          </button>
        ))}
        <span className="mt-1.5 font-mono text-[8px] text-zinc-500 flex items-center gap-1"><Radio className="w-2.5 h-2.5 text-emerald-400 animate-pulse" />{clock.slice(0, 5)}</span>
      </aside>

      {/* frosted content panel */}
      <main className="pointer-events-auto absolute left-[76px] right-3 sm:right-6 top-14 bottom-3 sm:bottom-6 z-20 flex flex-col rounded-2xl border border-white/12 bg-black/60 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.65)] overflow-hidden">
        {/* header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <h1 className="font-bold uppercase tracking-[0.3em] text-sm">Admin Console</h1>
            <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] truncate">{ov?.server?.name || serverId} · operator pc:{ov?.operator?.playercode ?? "—"} · {TABS.find(([id]) => id === tab)?.[2]}</p>
          </div>
          <span data-testid="admin-live-badge" className={`ml-auto shrink-0 font-mono text-[9px] uppercase tracking-[0.2em] border px-2.5 py-1.5 flex items-center gap-1.5 ${ov?.live ? "border-emerald-500/40 text-emerald-400" : "border-white/15 text-zinc-500"}`}>
            {ov?.live ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {ov?.live ? "Server live" : "No heartbeat"}
          </span>
        </div>

        {err && !demo && <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center"><WifiOff className="w-6 h-6 text-red-400" /><p data-testid="admin-error" className="font-mono text-[11px] text-red-400 max-w-md leading-relaxed">{err}</p><p className="font-mono text-[9px] text-zinc-400 max-w-md">Staff get in by having the bridge mint an admin session for their playercode (role=admin). Server owners can also open this page without a token to preview.</p></div>}
        {!ov && !err && <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} /></div>}

        {ov && (
          <div className="flex-1 min-h-0 overflow-y-auto p-5 pt-4" style={{ scrollbarWidth: "thin" }}>
            {/* LIVE OPS */}
            {tab === "live" && (
              <div data-testid="admin-live" className="flex flex-col gap-4">
                <div className="flex gap-3 flex-wrap">
                  {[
                    [Users, `${world.players ?? 0}/${world.max_players ?? 64}`, "citizens"],
                    [CloudSun, world.weather || "—", world.map || "map"],
                    [Clock, world.game_time || "—", "in-game"],
                    [ShieldHalf, `${(world.on_duty?.police ?? 0)} PD · ${(world.on_duty?.ems ?? 0)} EMS · ${(world.on_duty?.fire ?? 0)} FD`, "on duty"],
                  ].map(([Icon, v, l], i) => (
                    <div key={i} className="border border-white/10 bg-black/40 px-4 py-2.5 flex items-center gap-3 rounded-lg">
                      <Icon className="w-4 h-4" style={{ color: ACCENT }} />
                      <div><p className="font-bold text-sm leading-none">{v}</p><p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</p></div>
                    </div>
                  ))}
                </div>

                <div className="border border-white/10 bg-black/40 rounded-lg overflow-hidden">
                  <p className="px-4 py-2.5 border-b border-white/10 font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500">Live roster — {(world.roster || []).length} connected · click a player for their card</p>
                  {(world.roster || []).length === 0 && <p className="p-6 font-mono text-[10px] text-zinc-400 uppercase tracking-[0.2em]">Roster empty — the bridge sends it with each heartbeat (hub_state.json → "roster").</p>}
                  {(world.roster || []).map((p, i) => (
                    <div key={p.playercode || i} data-testid={`roster-row-${i}`} role="button" tabIndex={0}
                      onClick={() => setSelPlayer(p)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelPlayer(p); }}
                      className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 hover:bg-white/[0.04] transition-colors flex-wrap cursor-pointer">
                      <span className="font-mono text-[10px] text-zinc-500 w-16">#{p.playercode}</span>
                      <span className="font-semibold text-sm text-zinc-100 min-w-32">{p.name}</span>
                      <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${JOB_CLS[p.job] || JOB_CLS.civ}`}>{p.job || "civ"}</span>
                      <span className="font-mono text-[9px] text-zinc-500">{p.ping}ms</span>
                      {p.network_flags > 0 && (
                        <span data-testid={`roster-netflag-${i}`} className="font-mono text-[8px] uppercase tracking-[0.15em] border border-red-500/50 text-red-400 px-1.5 py-0.5 flex items-center gap-1" title={(p.flag_categories || []).join(", ")}>
                          <Flag className="w-2.5 h-2.5" /> {p.network_flags}x network
                        </span>
                      )}
                      <span className="ml-auto flex gap-1 items-center" onClick={(e) => e.stopPropagation()}>
                        {ACTIONS.map((a) => (
                          <button key={a.id} data-testid={`roster-${a.id}-${i}`} title={a.label}
                            onClick={() => { setAction({ actionId: a.id, target: p }); setReason(""); }}
                            className="border border-white/10 hover:border-white/40 p-1.5 transition-colors" style={{ color: a.color }}>
                            <a.Icon className="w-3.5 h-3.5" />
                          </button>
                        ))}
                        <ChevronRight className="w-3.5 h-3.5 text-zinc-600 ml-1" />
                      </span>
                    </div>
                  ))}
                </div>

                {/* action bar */}
                {action && (
                  <div data-testid="admin-action-bar" className="border p-4 flex items-center gap-3 flex-wrap rounded-lg" style={{ borderColor: `${ACCENT}55`, backgroundColor: "rgba(244,63,94,0.07)", animation: "ncFadeUp 0.2s ease both" }}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em]"><span style={{ color: ACCENT }}>{action.actionId}</span> → {action.target.name} <span className="text-zinc-500">#{action.target.playercode}</span></p>
                    {ACTIONS.find((a) => a.id === action.actionId)?.needsReason && (
                      <input data-testid="admin-action-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Reason (shown to the player)"
                        className="flex-1 min-w-48 bg-black border border-white/15 px-3 py-2 font-mono text-[11px] focus:outline-none focus:ring-1" style={{ "--tw-ring-color": ACCENT }} />
                    )}
                    <button data-testid="admin-action-confirm" disabled={busy || (ACTIONS.find((a) => a.id === action.actionId)?.needsReason && reason.trim().length < 3)} onClick={confirmAction}
                      className="px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-black disabled:opacity-40" style={{ backgroundColor: ACCENT }}>
                      Execute
                    </button>
                    <button data-testid="admin-action-cancel" onClick={() => setAction(null)} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-zinc-200">Cancel</button>
                  </div>
                )}
              </div>
            )}

            {/* WORLD */}
            {tab === "world" && (
              <div data-testid="admin-world" className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl">
                <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3 rounded-lg">
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2"><Megaphone className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Server announcement</p>
                  <textarea data-testid="admin-announce-input" rows={3} value={announce} onChange={(e) => setAnnounce(e.target.value)} maxLength={300}
                    placeholder="Broadcast to every citizen…" className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] resize-none focus:outline-none" style={{ "--tw-ring-color": ACCENT }} />
                  <button data-testid="admin-announce-send" disabled={announce.trim().length < 3} onClick={() => sendCommand("announce", {}, {}, announce).then((ok) => ok && setAnnounce(""))}
                    className="w-fit px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-black disabled:opacity-40" style={{ backgroundColor: ACCENT }}>Broadcast</button>
                </div>
                <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-4 rounded-lg">
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2 flex items-center gap-2"><CloudSun className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Weather</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {WEATHERS.map((w) => (
                        <button key={w} data-testid={`admin-weather-${w}`} onClick={() => setWeather(w)}
                          className={`px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] border transition-colors ${weather === w ? "text-black" : "border-white/15 text-zinc-400"}`}
                          style={weather === w ? { backgroundColor: ACCENT, borderColor: ACCENT } : {}}>{w}</button>
                      ))}
                    </div>
                    <button data-testid="admin-weather-apply" onClick={() => sendCommand("set_weather", {}, { weather })} className="mt-2 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 transition-colors">Apply weather</button>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2 flex items-center gap-2"><Clock className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Time of day — {String(hour).padStart(2, "0")}:00</p>
                    <input data-testid="admin-time-slider" type="range" min="0" max="23" value={hour} onChange={(e) => setHour(Number(e.target.value))} className="w-full accent-[#f43f5e]" />
                    <button data-testid="admin-time-apply" onClick={() => sendCommand("set_time", {}, { hour })} className="mt-2 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 transition-colors">Set time</button>
                  </div>
                </div>
              </div>
            )}

            {/* ECONOMY */}
            {tab === "economy" && eco && (
              <div data-testid="admin-economy" className="border border-white/10 bg-black/40 p-5 max-w-xl flex flex-col gap-1 rounded-lg">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2"><Banknote className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Economy tuning — bridge applies on next config pull</p>
                {[["starting_cash", "Starting cash ($)", 0, 1000000], ["paycheck_amount", "Paycheck ($)", 0, 100000], ["tax_income", "Income tax (%)", 0, 100], ["tax_sales", "Sales tax (%)", 0, 100], ["tax_vehicle", "Vehicle tax (%)", 0, 100]].map(([k, label, min, max]) => (
                  <div key={k} className="flex items-center justify-between gap-4 py-2.5 border-b border-white/5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
                    <input data-testid={`eco-${k}`} type="number" min={min} max={max} value={eco[k] ?? 0} onChange={(e) => setEco({ ...eco, [k]: Number(e.target.value) })}
                      className="w-32 bg-black border border-white/15 px-3 py-1.5 font-mono text-[11px] text-right focus:outline-none" />
                  </div>
                ))}
                <button data-testid="eco-save" disabled={busy} onClick={saveEconomy} className="mt-4 w-fit px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-black disabled:opacity-40" style={{ backgroundColor: ACCENT }}>Save economy</button>
              </div>
            )}

            {/* WHITELIST */}
            {tab === "whitelist" && (
              <div data-testid="admin-whitelist" className="flex flex-col gap-3 max-w-3xl">
                {whitelist.length === 0 && <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.25em] py-8 text-center">No pending applications</p>}
                {whitelist.map((a) => (
                  <div key={a.id} data-testid={`wl-app-${a.id}`} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2 rounded-lg">
                    <div className="flex items-center gap-3"><p className="font-semibold text-sm">{a.callsign}</p><span className="font-mono text-[8px] uppercase border border-white/15 text-zinc-500 px-1.5 py-0.5">{a.age_bracket}</span></div>
                    <p className="font-mono text-[10px] text-zinc-400 leading-relaxed"><span className="text-zinc-400 uppercase text-[8px] tracking-[0.2em]">Experience — </span>{a.experience}</p>
                    <p className="font-mono text-[10px] text-zinc-400 leading-relaxed"><span className="text-zinc-400 uppercase text-[8px] tracking-[0.2em]">Why — </span>{a.why}</p>
                    <div className="flex gap-2 mt-1">
                      <button data-testid={`wl-accept-${a.id}`} onClick={() => decide(a.id, "accepted")} className="px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] bg-emerald-500 text-black flex items-center gap-1.5"><UserCheck className="w-3 h-3" /> Accept</button>
                      <button data-testid={`wl-deny-${a.id}`} onClick={() => decide(a.id, "denied")} className="px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-red-500/50 text-red-400 flex items-center gap-1.5"><UserX className="w-3 h-3" /> Deny</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* BANS */}
            {tab === "bans" && (
              <div data-testid="admin-bans" className="flex flex-col gap-2 max-w-3xl">
                {bans.length === 0 && <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.25em] py-8 text-center">No active bans — clean city</p>}
                {bans.map((b) => (
                  <div key={b.playercode} data-testid={`ban-row-${b.playercode}`} className="border border-red-500/25 bg-red-500/5 px-4 py-3 flex items-center gap-3 flex-wrap rounded-lg">
                    <Ban className="w-4 h-4 text-red-400" />
                    <span className="font-semibold text-sm">{b.name || "—"}</span>
                    <span className="font-mono text-[10px] text-zinc-500">#{b.playercode}</span>
                    <span className="font-mono text-[10px] text-zinc-400 flex-1 min-w-40">{b.reason}</span>
                    <button data-testid={`unban-${b.playercode}`} onClick={() => sendCommand("unban", { playercode: b.playercode, name: b.name })}
                      className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/20 hover:border-emerald-500/60 hover:text-emerald-400 transition-colors">Unban</button>
                  </div>
                ))}
              </div>
            )}

            {/* NETWORK — Specpixel-network watch reports */}
            {tab === "network" && (
              <div data-testid="admin-network" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3 rounded-lg">
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2"><Flag className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Flag a player network-wide</p>
                  <p className="font-mono text-[9px] text-zinc-500 leading-relaxed uppercase tracking-[0.1em]">Goes to Specpixel staff with evidence. If approved, every server's admin console flags this player on sight.</p>
                  {netrep.target ? (
                    <div data-testid="netrep-target" className="border border-white/15 bg-black/50 px-3.5 py-2.5 flex items-center gap-3 rounded">
                      <Fingerprint className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                      <span className="font-semibold text-sm">{netrep.target.name}</span>
                      <span className="font-mono text-[10px] text-zinc-500">#{netrep.target.playercode}</span>
                      {netrep.target.steamid && <span className="font-mono text-[9px] text-zinc-500 truncate">steam {netrep.target.steamid}</span>}
                      <button data-testid="netrep-target-clear" onClick={() => setNetrep({ ...netrep, target: null })} className="ml-auto text-zinc-500 hover:text-zinc-200"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <button data-testid="netrep-pick" onClick={() => setTab("live")} className="border border-dashed border-white/20 hover:border-white/50 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-100 transition-colors text-left">
                      ← Pick the player from the Live Ops roster first
                    </button>
                  )}
                  <div className="flex gap-1.5 flex-wrap">
                    {REPORT_CATEGORIES.map((c) => (
                      <button key={c} data-testid={`netrep-category-${c.replace(/[^a-z]/gi, "-")}`} onClick={() => setNetrep({ ...netrep, category: c })}
                        className={`px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] border transition-colors ${netrep.category === c ? "text-black" : "border-white/15 text-zinc-400 hover:border-white/40"}`}
                        style={netrep.category === c ? { backgroundColor: ACCENT, borderColor: ACCENT } : {}}>{c}</button>
                    ))}
                  </div>
                  <textarea data-testid="netrep-reason" rows={3} value={netrep.reason} onChange={(e) => setNetrep({ ...netrep, reason: e.target.value })} maxLength={1000}
                    placeholder="What happened? (min 10 chars)" className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] resize-none focus:outline-none" />
                  <input data-testid="netrep-evidence-url" value={netrep.evidence_url} onChange={(e) => setNetrep({ ...netrep, evidence_url: e.target.value })} maxLength={500}
                    placeholder="Evidence link — clip / screenshot URL (optional if statement below)" className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] focus:outline-none" />
                  <textarea data-testid="netrep-evidence-text" rows={2} value={netrep.evidence_text} onChange={(e) => setNetrep({ ...netrep, evidence_text: e.target.value })} maxLength={1500}
                    placeholder="…or a written evidence statement (min 20 chars)" className="bg-black border border-white/15 px-3 py-2.5 font-mono text-[11px] resize-none focus:outline-none" />
                  <button data-testid="netrep-submit" disabled={busy || !netrep.target} onClick={fileReport}
                    className="w-fit px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-black disabled:opacity-40 flex items-center gap-2" style={{ backgroundColor: ACCENT }}>
                    <Flag className="w-3.5 h-3.5" /> File network report
                  </button>
                </div>

                <div className="border border-white/10 bg-black/40 rounded-lg overflow-hidden self-start">
                  <p className="px-4 py-2.5 border-b border-white/10 font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500">Filed from this server — {reports.length}</p>
                  {reports.length === 0 && <p className="p-6 font-mono text-[10px] text-zinc-400 uppercase tracking-[0.2em]">No reports filed yet.</p>}
                  {reports.map((r) => (
                    <div key={r.id} data-testid={`netrep-row-${r.id.slice(0, 8)}`} className="px-4 py-3 border-b border-white/5 flex flex-col gap-1.5">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="font-semibold text-sm text-zinc-100">{r.target_name || "—"}</span>
                        <span className="font-mono text-[10px] text-zinc-500">#{r.target_playercode}</span>
                        <span className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-1.5 py-0.5">{r.category}</span>
                        <span className={`ml-auto font-mono text-[8px] uppercase border px-1.5 py-0.5 ${REP_STATUS[r.status] || REP_STATUS.pending} ${r.status === "pending" ? "animate-pulse" : ""}`}>{r.status}</span>
                      </div>
                      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{r.reason}</p>
                      {r.review_note && <p className="font-mono text-[9px] text-zinc-500"><span className="uppercase tracking-[0.15em] text-zinc-400">{r.reviewed_by || "Staff"}:</span> {r.review_note}</p>}
                      <p className="font-mono text-[8px] text-zinc-600">{String(r.created_at).slice(0, 16).replace("T", " ")}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LOG */}
            {tab === "log" && (
              <div data-testid="admin-log" className="flex flex-col gap-1.5 max-w-3xl">
                {commands.length === 0 && <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.25em] py-8 text-center">No commands issued yet</p>}
                {commands.map((c) => (
                  <div key={c.id} data-testid={`log-${c.id}`} className="border border-white/10 bg-black/40 px-4 py-2.5 flex items-center gap-3 flex-wrap rounded-lg">
                    <span className="font-mono text-[10px] uppercase font-bold" style={{ color: ACCENT }}>{c.type}</span>
                    <span className="font-mono text-[10px] text-zinc-300">{c.target_name || (c.target_playercode ? `#${c.target_playercode}` : c.params?.weather || (c.params?.hour != null ? `${c.params.hour}:00` : "—"))}</span>
                    <span className="font-mono text-[9px] text-zinc-500 flex-1 min-w-32 truncate">{c.reason || ""}</span>
                    <span className="font-mono text-[8px] text-zinc-400">{c.by} · {String(c.created_at).slice(11, 19)}</span>
                    <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${CMD_STATUS[c.status] || CMD_STATUS.pending} ${c.status === "pending" ? "animate-pulse" : ""}`}>{c.status}{c.status === "failed" && c.error ? `: ${c.error}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* player detail card — floats right, frosted */}
      {selPlayer && (
        <div data-testid="player-card" className="pointer-events-auto absolute right-3 sm:right-8 top-16 w-[340px] z-40 rounded-2xl border border-white/15 bg-black/75 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.75)] overflow-hidden" style={{ animation: "ncSlideR 0.25s cubic-bezier(0.16,1,0.3,1) both" }}>
          <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${ACCENT}, transparent)` }} />
          <div className="p-5 flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl border border-white/15 bg-white/5 flex items-center justify-center font-bold text-lg" style={{ color: ACCENT }}>{(selPlayer.name || "?")[0]}</div>
              <div className="min-w-0 flex-1">
                <p data-testid="player-card-name" className="font-bold text-base text-zinc-50 truncate">{selPlayer.name}</p>
                <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em]">citizen #{selPlayer.playercode} · {selPlayer.job || "civ"} · {selPlayer.ping ?? "—"}ms</p>
              </div>
              <button data-testid="player-card-close" onClick={() => setSelPlayer(null)} className="text-zinc-500 hover:text-zinc-100 transition-colors"><X className="w-4 h-4" /></button>
            </div>

            {selPlayer.network_flags > 0 && (
              <div data-testid="player-card-flags approved-network-flag" className="border border-red-500/40 bg-red-500/10 px-3.5 py-2.5 rounded-lg flex items-center gap-2.5">
                <Flag className="w-4 h-4 text-red-400 shrink-0" />
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-red-300 leading-relaxed">
                  {selPlayer.network_flags}x approved network flag{(selPlayer.flag_categories || []).length ? ` — ${selPlayer.flag_categories.join(", ")}` : ""}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              {[
                ["Steam ID", selPlayer.steamid, "player-steamid"],
                ["IP address", selPlayer.ip, "player-ip"],
              ].map(([label, value, testid]) => (
                <div key={testid} className="flex items-center gap-2 border border-white/10 bg-black/50 px-3 py-2 rounded">
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 w-16 shrink-0">{label}</span>
                  <span data-testid={testid} className="font-mono text-[10px] text-zinc-200 truncate flex-1">{value || "—"}</span>
                  {value && <button data-testid={`${testid}-copy`} onClick={() => copyText(value, label)} className="text-zinc-500 hover:text-zinc-100 transition-colors shrink-0"><Copy className="w-3 h-3" /></button>}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {ACTIONS.map((a) => (
                <button key={a.id} data-testid={`card-${a.id}`} onClick={() => { setAction({ actionId: a.id, target: selPlayer }); setReason(""); setSelPlayer(null); setTab("live"); }}
                  className="border border-white/10 hover:border-white/40 py-2 flex flex-col items-center gap-1 transition-colors rounded" style={{ color: a.color }}>
                  <a.Icon className="w-3.5 h-3.5" />
                  <span className="font-mono text-[7px] uppercase tracking-[0.15em]">{a.label}</span>
                </button>
              ))}
            </div>

            <button data-testid="player-report-btn" onClick={() => openReportFor(selPlayer)}
              className="w-full py-2.5 font-mono text-[9px] uppercase tracking-[0.25em] border flex items-center justify-center gap-2 transition-colors hover:bg-red-500/10"
              style={{ borderColor: `${ACCENT}66`, color: ACCENT }}>
              <Flag className="w-3.5 h-3.5" /> Report to Specpixel network
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
